import { RanaModal, RanaTable } from "@nens/ranalib";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { useProjectFiles } from "@/hooks/use-project-files";
import { RanaFileIcon } from "@/lib/rana-file-data-type";
import { cancelButtonText, fileNameColumnString } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { getDuplicateFileId } from "@/utils/get-duplicate-file-id";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { useDuplicateProjectFilesOrDirectories } from "../api/duplicate";

interface ProjectFileOrDirectoryCopyToModalProps {
  items: ProjectFileOrDirectory[];
  onClose: () => void;
  initialPaths: string[];
}

export function ProjectFileOrDirectoryCopyToModal({
  items,
  initialPaths,
  onClose,
}: ProjectFileOrDirectoryCopyToModalProps) {
  const { isMutating, mutateAsync } = useDuplicateProjectFilesOrDirectories();
  const { paths, setPaths, path, intl, files, tableDataIds, onDirectoryOrFileClick } =
    useProjectFiles({
      initialPaths,
    });

  const copyFilesOrDirectoriesTo = async () => {
    await mutateAsync(
      items.map(({ id, type }, index) => {
        // Include names of all previously duplicated items to avoid name clashes
        const previousItemDuplicationIds = items
          .slice(0, index)
          .map(({ id }): string => getDuplicateFileId(path + getNameFromFileId(id), tableDataIds));
        const allIds = [...tableDataIds, ...previousItemDuplicationIds];

        return {
          sourceId: id,
          type,
          destinationId: getDuplicateFileId(path + getNameFromFileId(id), allIds),
        };
      })
    );
    onClose();
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={intl.formatMessage({
        id: "title.copy_files_directories_to",
        defaultMessage: "Copy files / folders to",
      })}
      maxW="xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: () => onClose(),
        },
        {
          variant: "primary",
          isDisabled: isMutating,
          onClick: copyFilesOrDirectoriesTo,
          children: intl.formatMessage({ id: "button.copy_to", defaultMessage: "Copy to" }),
        },
      ]}
    >
      <FoldersBreadcrumb paths={paths} setPaths={setPaths} />
      <RanaTable
        columnDefinitions={[
          {
            width: "table_cell",
            title: fileNameColumnString(intl),
          },
        ]}
        tableDataIds={tableDataIds}
        emptyElement={<TableEmptyFiles />}
        isLoading={isMutating}
      >
        <RanaTable.Container mt={4}>
          <RanaTable.Header />
          <RanaTable.Body>
            {files.map((fileOrDirectory) => {
              const name = getNameFromFileId(fileOrDirectory.id);
              return (
                <RanaTable.Row
                  isDisabled={fileOrDirectory.type === "file"}
                  onClick={() => onDirectoryOrFileClick(fileOrDirectory)}
                  key={fileOrDirectory.id}
                  rowId={fileOrDirectory.id}
                >
                  <RanaTable.Cell>
                    <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                    {name}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
}
